"use client";

import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { Spinner } from "@heroui/react";

import { logger } from "@/lib/secure-logger";
import { findFirstQuestion } from "@/lib/journey-utils";

import { JourneyQuestion, UserJourneyData } from "../../types/dataSchema";
import { useJourneyData } from "../JourneyDataClientProvider";

import { getNextQuestionId } from "./nextQuestionAlgorithm";
import { parseUrlParams, buildQuestionHistory } from "./utils/urlParamUtils";

export interface Answers {
  [key: string]: string | string[];
}

interface AnswerContextType {
  journeyData: UserJourneyData | null;
  currentQuestionId: string | null;
  currentQuestionConfig: JourneyQuestion | undefined;
  answers: Answers;
  questionHistory: string[];
  visitedQuestions: Set<string>;
  progressPercent: number;
  showRecommendations: boolean;
  typedQuestion: string;
  prefilledAnswers: Set<string>; // Track which answers came from URL params
  setTypedQuestion: Dispatch<SetStateAction<string>>;
  handleSelectAnswer: (
    questionId: string,
    answerValue: string | string[],
  ) => void;
  handleGoBack: () => void;
  handleGoBackToQuestion: (questionId: string) => void;
  getQuestionConfigById: (id: string | null) => JourneyQuestion | undefined;
  isWelcomeScreenDismissed: boolean;
  dismissWelcomeScreen: () => void;
  isQuestionRevisited: (questionId: string | null) => boolean;
}

const AnswerContext = createContext<AnswerContextType | undefined>(undefined);

export const useAnswerContext = () => {
  const context = useContext(AnswerContext);

  if (!context) {
    throw new Error("useAnswerContext must be used within an AnswerProvider");
  }

  return context;
};

interface AnswerProviderProps {
  children: ReactNode;
}

export const AnswerProvider: React.FC<AnswerProviderProps> = ({ children }) => {
  const { userData: initialJourneyData } = useJourneyData();
  const [journeyData, setJourneyData] = useState<UserJourneyData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const [currentQuestionId, setCurrentQuestionId] = useState<string | null>(
    null,
  );
  const [answers, setAnswers] = useState<Answers>({});
  const [questionHistory, setQuestionHistory] = useState<string[]>([]);
  const [visitedQuestions, setVisitedQuestions] = useState<Set<string>>(
    new Set(),
  );
  const [showRecommendations, setShowRecommendations] = useState(false);
  // Using a ref for the deeper question queue to allow direct mutation and avoid stale state issues.
  const deeperQuestionQueueRef = useRef<string[]>([]);
  const [typedQuestion, setTypedQuestion] = useState("");
  const [isWelcomeScreenDismissed, setIsWelcomeScreenDismissed] =
    useState(false);
  const [prefilledAnswers, setPrefilledAnswers] = useState<Set<string>>(
    new Set(),
  );

  // This state triggers re-renders when deeperQuestionQueueRef.current is updated.
  const [queueUpdateTrigger, setQueueUpdateTrigger] = useState(0);

  // Handle search params on client side only
  const [searchParams, setSearchParams] = useState<URLSearchParams | null>(
    null,
  );

  // Initialize search params on client side
  useEffect(() => {
    if (typeof window !== "undefined") {
      const urlSearchParams = new URLSearchParams(window.location.search);

      logger.debug(
        "Initializing search params:",
        Array.from(urlSearchParams.entries()),
      );

      setSearchParams(urlSearchParams);
    }
  }, []);

  const calculateProgressPercent = useCallback(() => {
    if (!journeyData?.questions || journeyData.questions.length === 0) {
      return 0;
    }

    const answeredQuestions = Object.keys(answers).filter((questionId) => {
      const answer = answers[questionId];

      if (Array.isArray(answer)) {
        return answer.length > 0;
      }

      return answer !== undefined && answer !== null && answer !== "";
    });

    // Simple approach: track the questions in the user's actual journey path
    // Start with all questions the user has visited or will visit based on their answers
    const questionsInUserPath = new Set<string>();

    // Add all answered questions to the path
    answeredQuestions.forEach((qId) => questionsInUserPath.add(qId));

    // If we have a current question, add it
    if (currentQuestionId) {
      questionsInUserPath.add(currentQuestionId);
    }

    // Add questions that are directly reachable from current position
    if (currentQuestionId) {
      const currentQuestion = journeyData.questions.find(
        (q) => q.id === currentQuestionId,
      );

      if (currentQuestion?.nextTopicQuestionId) {
        questionsInUserPath.add(currentQuestion.nextTopicQuestionId);
      }
    }

    // Use the path size as total, with a minimum of answered questions + 1
    const totalQuestionsInPath = Math.max(
      questionsInUserPath.size,
      answeredQuestions.length + 1,
    );

    // Calculate progress
    const progressPercent =
      (answeredQuestions.length / totalQuestionsInPath) * 100;

    return Math.min(100, Math.max(0, progressPercent));
  }, [journeyData, answers, currentQuestionId]);

  const progressPercent = useMemo(() => {
    return calculateProgressPercent();
  }, [calculateProgressPercent]);

  const getQuestionConfigById = useCallback(
    (
      id: string | null,
      data: UserJourneyData | null = journeyData,
    ): JourneyQuestion | undefined => {
      if (!id || !data) {
        return undefined;
      }

      return data.questions.find((q) => q.id === id);
    },
    [journeyData],
  );

  // Initial journey data load and first question setup with URL parameter support.
  useEffect(() => {
    logger.debug("AnswerContext useEffect triggered", {
      hasInitialJourneyData: !!initialJourneyData,
      currentQuestionId,
      hasSearchParams: !!searchParams,
    });

    if (initialJourneyData) {
      setJourneyData(initialJourneyData);

      // Process URL parameters if available and not already processed
      if (
        !Object.keys(answers).length &&
        initialJourneyData.questions?.length > 0
      ) {
        // Check for URL parameters only if searchParams is available (client-side)
        if (searchParams && Array.from(searchParams.entries()).length > 0) {
          logger.debug("Parsing URL params with searchParams available");

          const urlParamData = parseUrlParams(searchParams, initialJourneyData);

          logger.debug("URL param data parsed:", urlParamData);

          if (urlParamData.hasErrors) {
            // If there were errors, show a toast and continue with normal flow
            const firstQuestion = findFirstQuestion(initialJourneyData);

            if (firstQuestion) {
              setCurrentQuestionId(firstQuestion.id);
            }
          } else if (Object.keys(urlParamData.answers).length > 0) {
            // URL parameters found and valid - prefill answers
            setAnswers(urlParamData.answers);

            // Track which answers were prefilled from URL
            setPrefilledAnswers(new Set(Object.keys(urlParamData.answers)));

            // Build question history based on prefilled answers
            const history = buildQuestionHistory(
              urlParamData.answers,
              initialJourneyData,
            );

            setQuestionHistory(history);

            // Mark prefilled questions as visited
            const visitedSet = new Set(history);

            setVisitedQuestions(visitedSet);

            // Set the starting question (either next unanswered or show recommendations)
            if (urlParamData.startingQuestionId === "SHOW_RECOMMENDATIONS") {
              setCurrentQuestionId("SHOW_RECOMMENDATIONS");
              setShowRecommendations(true);
            } else {
              setCurrentQuestionId(urlParamData.startingQuestionId);
              setShowRecommendations(false);
            }
          } else {
            // No URL parameters - start normally
            logger.debug(
              "No URL parameters found, starting with first question",
            );
            if (!currentQuestionId) {
              const firstQuestion = findFirstQuestion(initialJourneyData);

              if (firstQuestion) {
                setCurrentQuestionId(firstQuestion.id);
              }
            }
          }
        } else {
          // searchParams not available (SSR) - start normally
          logger.debug(
            "SearchParams not available, starting with first question",
          );
          if (!currentQuestionId) {
            const firstQuestion = findFirstQuestion(initialJourneyData);

            if (firstQuestion) {
              setCurrentQuestionId(firstQuestion.id);
            }
          }
        }
      } else if (
        !currentQuestionId &&
        initialJourneyData.questions?.length > 0
      ) {
        // No answers yet and no current question - start with first question
        const firstQuestion = findFirstQuestion(initialJourneyData);

        if (firstQuestion) {
          setCurrentQuestionId(firstQuestion.id);
        }
      }
      setIsLoading(false);
    } else {
      logger.debug("No initial journey data available yet");
      setIsLoading(true);
    }
  }, [initialJourneyData, currentQuestionId, searchParams]);

  const currentQuestionConfig = getQuestionConfigById(currentQuestionId);

  const handleSelectAnswer = useCallback(
    (questionId: string, answerValue: string | string[]) => {
      if (!journeyData) {
        return;
      }

      // Handle skip case specially
      const isSkipped = answerValue === "(skipped)";

      setAnswers((prevAnswers) => {
        const newAnswers = {
          ...prevAnswers,
          [questionId]: answerValue,
        };

        // Mark that user has answered at least one question (for feedback button visibility)
        try {
          sessionStorage.setItem("hasAnsweredOnboardingQuestions", "true");
        } catch {
          // Storage not available
        }

        return newAnswers;
      });

      setQuestionHistory((prev) => {
        if (prev[prev.length - 1] !== questionId) {
          return [...prev, questionId];
        }

        return prev;
      });

      const questionConfig = getQuestionConfigById(questionId);

      if (!questionConfig) {
        // eslint-disable-next-line no-console
        console.warn(`Question config not found for ID: ${questionId}`);
        setCurrentQuestionId("SHOW_RECOMMENDATIONS");

        return;
      }

      // Prepare selected options for the algorithm
      const selectedValues = isSkipped
        ? []
        : Array.isArray(answerValue)
          ? answerValue
          : answerValue
            ? [answerValue]
            : [];

      // Compute next question using the new algorithm
      const nextId = getNextQuestionId({
        questions: journeyData.questions,
        questionHistory: [...questionHistory, questionId],
        currentQuestionId: questionId,
        skipped: isSkipped,
        selectedOptions: selectedValues,
        answers: { ...answers, [questionId]: answerValue },
      });

      if (nextId) {
        setCurrentQuestionId(nextId);
        setShowRecommendations(false);
      } else {
        setCurrentQuestionId("SHOW_RECOMMENDATIONS");
        setShowRecommendations(true);
      }
    },
    [answers, journeyData, getQuestionConfigById, questionHistory],
  );

  const handleGoBack = useCallback(() => {
    if (questionHistory.length > 0) {
      // Get the current history
      const currentHistory = [...questionHistory];

      // If the current question is the same as the last in history, we need to go back 2 steps
      // Otherwise, go back 1 step
      const lastInHistory = currentHistory[currentHistory.length - 1];

      if (currentQuestionId === lastInHistory) {
        // Current question is in history, remove it and go to previous
        currentHistory.pop();
      }

      // Now get the previous question
      const prevQuestionId = currentHistory[currentHistory.length - 1];

      // Special case: if we're going back to the first question
      const firstQuestion = journeyData && findFirstQuestion(journeyData);
      const isGoingToFirstQuestion =
        firstQuestion && prevQuestionId === firstQuestion.id;

      // Remove answers for questions that come after the target question in history
      // This ensures the progress bar reflects the correct state
      setAnswers((prevAnswers) => {
        const newAnswers = { ...prevAnswers };

        if (isGoingToFirstQuestion) {
          // If going to first question, clear all answers
          return {};
        } else {
          // Remove answers for questions not in the updated history
          Object.keys(newAnswers).forEach((questionId) => {
            if (!currentHistory.includes(questionId)) {
              delete newAnswers[questionId];
            }
          });
        }

        return newAnswers;
      });

      // Update history to reflect where we're going
      if (isGoingToFirstQuestion) {
        // Clear history when going to first question
        setQuestionHistory([]);
      } else {
        setQuestionHistory(currentHistory);
      }

      // Reset the deeper question queue and recommendations when going back.
      deeperQuestionQueueRef.current = [];
      setQueueUpdateTrigger((prev) => prev + 1);
      setShowRecommendations(false);

      if (prevQuestionId) {
        setCurrentQuestionId(prevQuestionId);
      } else if (
        journeyData &&
        journeyData.questions &&
        journeyData.questions.length > 0
      ) {
        // If history is empty, go back to the very first question.
        const firstQuestion = findFirstQuestion(journeyData);

        if (firstQuestion) {
          setCurrentQuestionId(firstQuestion.id);
        }
      } else {
        setCurrentQuestionId(null);
      }
    }
  }, [questionHistory, journeyData, currentQuestionId]);

  // Helper function to go back to a specific question
  const handleGoBackToQuestion = useCallback(
    (targetQuestionId: string) => {
      if (!journeyData) return;

      // Find the position in history
      const targetIndex = questionHistory.indexOf(targetQuestionId);

      if (targetIndex === -1) return;

      // Update history to only include questions up to target
      const newHistory = questionHistory.slice(0, targetIndex + 1);

      // Instead of removing all answers not in newHistory,
      // we only reset the questionHistory and currentQuestion.
      // This preserves answers for questions that might be revisited
      // in the new path, allowing for pre-filling.
      // The answers will be naturally cleaned up if the user
      // takes a different path that doesn't include those questions.

      setQuestionHistory(newHistory);
      setCurrentQuestionId(targetQuestionId);
      setShowRecommendations(false);

      // Reset deeper question queue
      deeperQuestionQueueRef.current = [];
      setQueueUpdateTrigger((prev) => prev + 1);
    },
    [journeyData, questionHistory],
  );

  // This useEffect manages the deeper question queue and transitions to next topics or recommendations.
  useEffect(() => {
    // If currentQuestionId is null (signaling to find next question) and deeper queue has items.
    if (
      currentQuestionId === null &&
      deeperQuestionQueueRef.current.length > 0
    ) {
      const nextIdFromQueue = deeperQuestionQueueRef.current.shift(); // Get and remove the first element.

      if (nextIdFromQueue) {
        // Ensure the question from queue hasn't been answered already to prevent loops.
        if (!questionHistory.includes(nextIdFromQueue)) {
          setCurrentQuestionId(nextIdFromQueue);
        } else {
          // If already in history, skip it and re-evaluate the queue.
          setQueueUpdateTrigger((prev) => prev + 1); // Trigger re-render to re-evaluate queue.
          setCurrentQuestionId(null); // Keep signal for useEffect.
        }
      }
    }
    // If currentQuestionId is null, deeper queue is empty, and not loading, it's time to find the next topic.
    else if (
      currentQuestionId === null &&
      deeperQuestionQueueRef.current.length === 0 &&
      !isLoading
    ) {
      const lastQuestionAnsweredId =
        questionHistory[questionHistory.length - 1];
      const lastQuestionConfig = getQuestionConfigById(lastQuestionAnsweredId);

      if (lastQuestionConfig?.nextTopicQuestionId) {
        setCurrentQuestionId(lastQuestionConfig.nextTopicQuestionId);
      } else {
        // If no nextTopicQuestionId, it's the end of the journey path, show recommendations.
        setShowRecommendations(true);
        setCurrentQuestionId(null);
      }
    } else if (currentQuestionId === "SHOW_RECOMMENDATIONS") {
      setShowRecommendations(true);
      setCurrentQuestionId(null);
      deeperQuestionQueueRef.current = []; // Clear the queue if navigating to recommendations.
      setQueueUpdateTrigger((prev) => prev + 1);
    }
  }, [
    currentQuestionId,
    questionHistory,
    isLoading,
    getQuestionConfigById,
    queueUpdateTrigger,
  ]);

  const dismissWelcomeScreen = () => {
    setIsWelcomeScreenDismissed(true);
  };

  const isQuestionRevisited = useCallback(
    (questionId: string | null) => {
      return questionId ? visitedQuestions.has(questionId) : false;
    },
    [visitedQuestions],
  );

  // Track visited questions when currentQuestionId changes
  useEffect(() => {
    if (currentQuestionId && !visitedQuestions.has(currentQuestionId)) {
      setVisitedQuestions((prev) => {
        const newSet = new Set(prev);

        newSet.add(currentQuestionId);

        return newSet;
      });
    }
  }, [currentQuestionId, visitedQuestions]);

  useEffect(() => {
    if (!currentQuestionConfig?.question || !isWelcomeScreenDismissed) {
      if (currentQuestionConfig?.question) {
        setTypedQuestion(currentQuestionConfig.question);
      } else {
        setTypedQuestion("");
      }

      return;
    }
    setTypedQuestion("");

    let i = 0;
    const fullText = currentQuestionConfig.question;
    let cancelled = false;

    function typeNext() {
      if (cancelled) {
        return;
      }
      setTypedQuestion(fullText.slice(0, i + 1));
      if (i < fullText.length - 1) {
        i++;
        setTimeout(typeNext, 15);
      }
    }
    const timer = setTimeout(typeNext, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [currentQuestionConfig?.question, isWelcomeScreenDismissed]);

  if (isLoading || !journeyData) {
    return (
      <div className="h-screen w-full flex items-center justify-center">
        <Spinner className="mx-auto mt-10" size="lg" />
      </div>
    );
  }

  const value: AnswerContextType = {
    journeyData,
    currentQuestionId,
    currentQuestionConfig,
    answers,
    questionHistory,
    visitedQuestions,
    progressPercent,
    showRecommendations,
    typedQuestion,
    prefilledAnswers,
    setTypedQuestion,
    handleSelectAnswer,
    handleGoBack,
    handleGoBackToQuestion,
    getQuestionConfigById,
    isWelcomeScreenDismissed,
    dismissWelcomeScreen,
    isQuestionRevisited,
  };

  return (
    <AnswerContext.Provider value={value}>{children}</AnswerContext.Provider>
  );
};
